import { useState, useEffect, useCallback } from "react";
import { apiClient } from "../../services/api";
import RefreshButton from "../RefreshButton/RefreshButton";

const VARIABLE_LABELS = {
  current_time: "📅 Current date/time",
  timezone: "🌍 User timezone",
  memories: "🧠 User's memories and preferences",
  upcoming_schedule: "📆 Upcoming schedule (next 7 days)",
  pending_reminders: "🔔 Pending reminders",
  routines: "🔄 Active routines",
  ideas: "💡 Open ideas",
  devices: "🏠 Smart devices",
  tkadlec_memories: "🧠 Tkadlec memories and preferences",
};

function PromptAnalysis() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [activeSection, setActiveSection] = useState("system");

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiClient.get("/prompt-logs?limit=50");
      setLogs(data.items || []);
    } catch (err) {
      console.error("Failed to fetch prompt logs:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const parseVariables = (varsJson) => {
    try {
      return typeof varsJson === "string" ? JSON.parse(varsJson) : varsJson;
    } catch {
      return {};
    }
  };

  const parseChatMessages = (chatJson) => {
    try {
      return typeof chatJson === "string" ? JSON.parse(chatJson) : chatJson;
    } catch {
      return [];
    }
  };

  const formatNumber = (n) => {
    if (typeof n === "number") return n.toLocaleString();
    if (typeof n === "string") return parseInt(n, 10).toLocaleString();
    return "0";
  };

  const toggleExpand = (id) => {
    setExpandedId(expandedId === id ? null : id);
    setActiveSection("system");
  };

  if (loading) {
    return <div className="placeholder">Loading prompt logs...</div>;
  }

  if (logs.length === 0) {
    return (
      <div className="placeholder">
        No prompt logs yet. Send a message to Anna to generate the first log.
      </div>
    );
  }

  return (
    <div className="prompt-analysis">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "1rem" }}>
        <p className="prompt-analysis-description">
          Every prompt sent to GPT-5.4 is captured here. Token estimates use ~4 chars/token approximation.
        </p>
        <RefreshButton onClick={fetchLogs} title="Refresh prompt logs" />
      </div>

      <div className="prompt-logs-list">
        {logs.map((log) => {
          const tokens = log.tokenEstimate || {};
          const chars = log.charCount || {};
          const isExpanded = expandedId === log.id;
          const variables = parseVariables(log.variables);
          const chatMessages = parseChatMessages(log.chatMessages);
          const memoryBlock = variables.tkadlec_memories || variables.memories || "";

          return (
            <div key={log.id} className={`prompt-log-item ${isExpanded ? "expanded" : ""}`}>
              <div className="prompt-log-header" onClick={() => toggleExpand(log.id)}>
                <div className="prompt-log-meta">
                  <span className="prompt-log-source">{log.source}</span>
                  <span className="prompt-log-time">
                    {log.timestamp ? new Date(log.timestamp).toLocaleString() : "—"}
                  </span>
                  <span className="prompt-log-session" title={log.sessionId}>
                    {log.sessionId?.slice(0, 16)}
                  </span>
                </div>
                <div className="prompt-log-preview">
                  {log.userMessage?.slice(0, 100)}{log.userMessage?.length > 100 ? "..." : ""}
                </div>
                <div className="prompt-log-tokens">
                  <span className="token-badge total">
                    ~{formatNumber(tokens.total)} tokens
                  </span>
                  <span className="token-badge system">
                    System: ~{formatNumber(tokens.system)}
                  </span>
                  <span className="token-badge chat">
                    Chat: ~{formatNumber(tokens.chat)}
                  </span>
                  <span className="token-badge skills">
                    Skills: ~{formatNumber(Math.round((variables.skills?.length || 0) / 4))}
                  </span>
                  <span className="token-badge user">
                    User: ~{formatNumber(tokens.user)}
                  </span>
                  <span className="char-count">
                    {formatNumber(chars.total)} chars
                  </span>
                </div>
              </div>

              {isExpanded && (
                <div className="prompt-log-detail">
                  <div className="section-tabs">
                    {["system", "variables", "memories", "chat", "skills", "user", "full"].map((s) => (
                      <button
                        key={s}
                        className={`section-tab ${activeSection === s ? "active" : ""}`}
                        onClick={() => setActiveSection(s)}
                      >
                        {s === "system" ? "Core Prompt" :
                         s === "variables" ? "Variables" :
                         s === "memories" ? "🧠 Memories" :
                         s === "chat" ? `Chat (${chatMessages.length})` :
                         s === "skills" ? "🧩 Skills" :
                         s === "user" ? "User Message" :
                         "Full System Prompt"}
                      </button>
                    ))}
                  </div>

                  <div className="section-content">
                    {activeSection === "system" && (
                      <div className="prompt-section">
                        <div className="section-header">
                          <h4>Core Prompt Template</h4>
                          <span className="section-stat">
                            ~{formatNumber(Math.round(log.corePromptTemplate?.length / 4))} tokens
                            ({formatNumber(log.corePromptTemplate?.length)} chars)
                          </span>
                        </div>
                        <pre className="prompt-content">{log.corePromptTemplate}</pre>
                      </div>
                    )}

                    {activeSection === "variables" && (
                      <div className="prompt-section">
                        <div className="section-header">
                          <h4>Context Variables</h4>
                          <span className="section-stat">
                            These are the values injected into the prompt template placeholders
                          </span>
                        </div>
                        <div className="variables-grid">
                          {Object.entries(variables).map(([key, value]) => {
                            const label = VARIABLE_LABELS[key] || key;
                            const isEmpty = !value || value === "None" || value === "0" || value === "500";
                            return (
                              <div key={key} className={`variable-item ${isEmpty ? "empty" : ""}`}>
                                <div className="variable-name">
                                  <span className="variable-label">{label}</span>
                                  <code className="variable-key">{`{${key}}`}</code>
                                </div>
                                <div className="variable-stats">
                                  {value?.length || 0} chars · ~{formatNumber(Math.round((value?.length || 0) / 4))} tokens
                                </div>
                                <pre className="variable-value">
                                  {value || "(empty)"}
                                </pre>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {activeSection === "memories" && (
                      <div className="prompt-section">
                        <div className="section-header">
                          <h4>Memories & Preferences</h4>
                          <span className="section-stat">
                            ~{formatNumber(Math.round(memoryBlock.length / 4))} tokens
                            ({formatNumber(memoryBlock.length)} chars)
                          </span>
                        </div>
                        {!memoryBlock || memoryBlock === "No memories stored yet." ? (
                          <p className="empty-section">No memories loaded</p>
                        ) : (
                          <pre className="prompt-content">{memoryBlock}</pre>
                        )}
                      </div>
                    )}

                    {activeSection === "chat" && (
                      <div className="prompt-section">
                        <div className="section-header">
                          <h4>Chat History ({chatMessages.length} messages)</h4>
                          <span className="section-stat">
                            ~{formatNumber(tokens.chat)} tokens
                          </span>
                        </div>
                        {chatMessages.length === 0 ? (
                          <p className="empty-section">No chat history (first message in session)</p>
                        ) : (
                          <div className="chat-messages-log">
                            {chatMessages.map((msg, i) => (
                              <div key={i} className={`chat-log-msg ${msg.role}`}>
                                <span className="chat-log-role">{msg.role}</span>
                                <pre className="chat-log-content">{msg.content}</pre>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}

                    {activeSection === "skills" && (
                      <div className="prompt-section">
                        <div className="section-header">
                          <h4>Loaded Skills</h4>
                          <span className="section-stat">
                            ~{formatNumber(Math.round((variables.skills?.length || 0) / 4))} tokens
                            ({formatNumber(variables.skills?.length || 0)} chars)
                          </span>
                        </div>
                        {!variables.skills ? (
                          <p className="empty-section">No skills loaded for this message</p>
                        ) : (
                          <pre className="prompt-content">{variables.skills}</pre>
                        )}
                      </div>
                    )}

                    {activeSection === "user" && (
                      <div className="prompt-section">
                        <div className="section-header">
                          <h4>User Message</h4>
                          <span className="section-stat">
                            ~{formatNumber(tokens.user)} tokens
                            ({formatNumber(chars.user)} chars)
                          </span>
                        </div>
                        <pre className="prompt-content">{log.userMessage}</pre>
                      </div>
                    )}

                    {activeSection === "full" && (
                      <div className="prompt-section">
                        <div className="section-header">
                          <h4>Full System Prompt (after variable substitution)</h4>
                          <span className="section-stat">
                            ~{formatNumber(tokens.system)} tokens
                            ({formatNumber(chars.system)} chars)
                          </span>
                        </div>
                        <pre className="prompt-content">{log.systemPromptFull}</pre>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default PromptAnalysis;
